import { useCallback, useSyncExternalStore } from 'react'
import type { ShaderInputType } from '@/lib/canvas/shader/media/contract'

/**
 * Which inputs of each Shader node have an edge, as its node component last
 * saw them. The settings panel renders outside React Flow's store on the web
 * canvas, so the node publishes what it reads from its connections here.
 */
const NONE: readonly ShaderInputType[] = []
const wired = new Map<string, readonly ShaderInputType[]>()
const listeners = new Map<string, Set<() => void>>()

function keyOf(scope: string, nodeId: string): string {
  return `${scope} ${nodeId}`
}

export function publishWiredShaderInputs(scope: string, nodeId: string, inputs: readonly ShaderInputType[]): void {
  const key = keyOf(scope, nodeId)
  if (wired.get(key) === inputs) return
  wired.set(key, inputs)
  listeners.get(key)?.forEach((listener) => listener())
}

export function forgetWiredShaderInputs(scope: string): void {
  const prefix = keyOf(scope, '')
  for (const key of [...wired.keys()]) if (key.startsWith(prefix)) wired.delete(key)
}

export function useWiredShaderInputs(scope: string, nodeId: string): readonly ShaderInputType[] {
  const key = keyOf(scope, nodeId)
  const subscribe = useCallback((listener: () => void) => {
    const set = listeners.get(key) ?? new Set()
    set.add(listener)
    listeners.set(key, set)
    return () => {
      set.delete(listener)
      if (!set.size) listeners.delete(key)
    }
  }, [key])
  return useSyncExternalStore(subscribe, () => wired.get(key) ?? NONE)
}
